import { useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useContent } from "@/contexts/ContentContext";
import { ICON_OPTIONS } from "@/lib/icons";

export const AboutEditor = () => {
  const { content, update } = useContent();
  const [draft, setDraft] = useState(content.about);
  const [busy, setBusy] = useState(false);

  const onField = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  const updateValue = (i, k, v) => {
    const values = [...(draft.values || [])];
    values[i] = { ...values[i], [k]: v };
    setDraft({ ...draft, values });
  };
  const addValue = () => setDraft({ ...draft, values: [...(draft.values || []), { icon: "Sparkles", title: "New value", desc: "Short description." }] });
  const removeValue = (i) => setDraft({ ...draft, values: (draft.values || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    setBusy(true);
    try {
      await update("about", draft);
      toast.success("About updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="about-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Eyebrow</Label>
          <Input data-testid="about-editor-eyebrow" value={draft.eyebrow || ""} onChange={onField("eyebrow")} />
        </div>
        <div className="space-y-1.5">
          <Label>Mission line</Label>
          <Input data-testid="about-editor-mission" value={draft.mission || ""} onChange={onField("mission")} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Title (line 1)</Label>
          <Input data-testid="about-editor-title1" value={draft.title_line1 || ""} onChange={onField("title_line1")} />
        </div>
        <div className="space-y-1.5">
          <Label>Title (line 2, accent)</Label>
          <Input data-testid="about-editor-title2" value={draft.title_line2 || ""} onChange={onField("title_line2")} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea rows={4} data-testid="about-editor-desc" value={draft.description || ""} onChange={onField("description")} />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-base">Values</Label>
          <Button type="button" variant="outline" size="sm" onClick={addValue} data-testid="about-editor-value-add" className="rounded-lg">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add value
          </Button>
        </div>
        <div className="mt-3 space-y-3">
          {(draft.values || []).map((v, i) => (
            <div key={i} data-testid={`about-editor-value-${i}`} className="rounded-xl border border-[hsl(var(--border))] bg-white p-4">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-3 space-y-1">
                  <Label className="text-xs">Icon</Label>
                  <Select value={v.icon || "Sparkles"} onValueChange={(val) => updateValue(i, "icon", val)}>
                    <SelectTrigger data-testid={`about-editor-value-${i}-icon`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3 space-y-1">
                  <Label className="text-xs">Title</Label>
                  <Input data-testid={`about-editor-value-${i}-title`} value={v.title || ""} onChange={(e) => updateValue(i, "title", e.target.value)} />
                </div>
                <div className="sm:col-span-5 space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input data-testid={`about-editor-value-${i}-desc`} value={v.desc || ""} onChange={(e) => updateValue(i, "desc", e.target.value)} />
                </div>
                <div className="sm:col-span-1 flex items-end justify-end">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeValue(i)} data-testid={`about-editor-value-${i}-remove`} className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A14]">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="about-editor-save" className="h-10 rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save About
        </Button>
      </div>
    </div>
  );
};

export default AboutEditor;
